import API_BASE_URL from "../config";

// Pure helpers for math/markdown rendering, kept free of the (ESM-only)
// react-markdown stack so they can be unit-tested in isolation. Consumed by
// MarkdownView.jsx.

// LaTeX syntax that never appears in a run of prose between two currency amounts.
const LATEX_SYNTAX = /[\\^_{}]/;

// A lone `$` (not escaped, not part of `$$`) followed by a digit, capturing the
// rest of the line up to the next `$` and that `$` itself if there is one.
const DIGIT_DOLLAR = /(?<![\\$])\$(?=(\d[^$\n]*)(\$?))/g;

// Escape a currency `$` (e.g. "$2,000") so remark-math never treats it as an
// inline-math delimiter. A `$` followed by a digit is still genuine math when
// it closes on the same line around LaTeX syntax ("$0,12 \text{ W/mK}$",
// "$30 \text{ N/mm}^2$") or around a single token ("$1,35$") — answers
// routinely state a value-with-unit as math, so escaping every `$<digit>`
// broke those formulas. Only the opening `$` is ever rewritten; matching by
// lookahead leaves the closing `$` to be judged as an opener in its own right.
function escapeCurrencyDollarSigns(content) {
  return content.replace(DIGIT_DOLLAR, (dollar, inner, closing) => {
    const isMath = Boolean(closing) && (LATEX_SYNTAX.test(inner) || !/\s/.test(inner));
    return isMath ? dollar : "\\$";
  });
}

// A `$$…$$` display or `$…$` inline math span. Escaped `\$` (currency, after
// escapeCurrencyDollarSigns) is never a delimiter.
const MATH_SPAN = /(?<![\\$])(\$\$?)(?!\$)([\s\S]*?)(?<![\\$])\1(?!\$)/g;

// \tag{...} (one level of nested braces) — KaTeX rejects it in inline math.
const TAG_COMMAND = /\\tag\s*\*?\s*\{[^{}]*(?:\{[^{}]*\}[^{}]*)?\}/g;

// A comma between two digits: a decimal ("0,12") or thousands separator. In
// math mode KaTeX sets a bare comma as punctuation followed by a space
// ("0, 12"); `{,}` makes it an ordinary symbol, the standard LaTeX idiom.
const DIGIT_COMMA = /(\d),(?=\d)/g;

function normalizeMathBody(inner) {
  return inner.replace(TAG_COMMAND, "").replace(DIGIT_COMMA, "$1{,}");
}

function normalizeMathSpans(content) {
  return content.replace(
    MATH_SPAN,
    (match, delimiter, inner) => delimiter + normalizeMathBody(inner) + delimiter,
  );
}

// Normalize markdown/LaTeX text so KaTeX parses and typesets it: strip
// \tag{...}, brace decimal commas inside math, and decode HTML entities that
// survive extraction into their LaTeX/character equivalents.
export function decodeLatexEntities(content) {
  return normalizeMathSpans(escapeCurrencyDollarSigns(content))
    .replace(/\\tag\s*\*?\s*\{[^}]*\}/g, "")
    .replace(/&gt;/g, ">")
    .replace(/&lt;/g, "<")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&le;/g, "\\leq")
    .replace(/&ge;/g, "\\geq")
    .replace(/&ne;/g, "\\neq");
}

// Rewrite a backend image reference to an absolute URL against API_BASE_URL.
// Leaves already-absolute / external srcs untouched.
export function resolveImageSrc(src) {
  if (!src) return src;
  const apiMatch = src.match(/\/api\/v1\/images\/[^\s)]+/);
  if (apiMatch) return `${API_BASE_URL}${apiMatch[0]}`;
  // Deliberately version-agnostic (not /api/v1/ specifically) — matches
  // regardless of API version, so this line doesn't need touching on a
  // future version bump.
  if (src.startsWith("/api/")) return `${API_BASE_URL}${src}`;
  return src;
}
